/**
 * 파일명 : evl0105m.jsx
 * 설명   : 인사평가(HR)
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용
 ************************************************
 * 1.0        2025-12-10    지재용    최초작성

 ************************************************
/**
 * Descriptions
 * 프로젝트 평가 시스템 - 인사평가 평가표 (evl0105m)
 *
 * 변경 사항:
 * 1. 인사평가(HR) 신규 작성
 */
/************************************************
 * Import 영역
 ************************************************/
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { message, Table, Select, Input, Button,  Modal, Spin } from 'antd';
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Evl0105mAction } from '../../../redux/slice/evl/evl0105m_slice';
import type { IPropsEvl0105M, UserInfoRow, EvalInfoRow, TaskRow, RowData} from '../../../redux/models/evl/evl0105m_model';
import { convertSelfDevelopmentToSaveItems } from 'redux/slice/evl/evl0102m_slice';



const { TextArea } = Input;
const gradeOptions = ['S', 'A', 'B', 'C', 'D'];   // 비중
const gradeScoreMap: Record<string, number> = { S: 1, A: 0.9, B: 0.8, C: 0.7, D: 0.6 }; 
const jobGroupMap: Record<string, string> = { "01": "비즈니스컨설턴트", "02": "전산전문직", "03": "일반직", };    // 직군코드 매핑 객체

// 선택 변경 핸들러
  const handleSelectChange = (
    value: string,
    recordKey: string,
    field: string,
    setData: React.Dispatch<React.SetStateAction<any[]>>
  ) => {
    setData(prev =>
      prev.map(row =>
        row.key === recordKey ? { ...row, [field]: value } : row
      )
    );
  };

  // 총합 계산 함수 (결과를 소수점 2자리 문자열로 변환)
const calcTotal = (data: any[]) => {
  const sum = { self: 0, first: 0, second: 0, weight: 0 };
  data.forEach(row => {
    const w = Number(row.weight || 0);
    sum.weight += w;
    if (row.self) sum.self += (gradeScoreMap[row.self] || 0) * w;
    if (row.first) sum.first += (gradeScoreMap[row.first] || 0) * w;
    if (row.second) sum.second += (gradeScoreMap[row.second] || 0) * w;
  });

  return {
    weight: sum.weight,
    // 계산된 숫자를 문자열로 변환하여 리턴
    self: sum.self.toFixed(2),
    first: sum.first.toFixed(2),
    second: sum.second.toFixed(2),
  };
};

export default function Evl0105m(props: IPropsEvl0105M) {
  const { hrMapId, evlYear, evlEnableYn, evlEditableYn, evlType } = props;
  const loginEmpNo = common.getEmpNo();     
  const hrEvlItmId = "";                  
  // 미리보기 모드
  // const evlCd = props.evlType === "HR-Junior" ? "02" : "01";
  const isPreview = props.viewMode === "preview";

  const currentYear = new Date().getFullYear();                                         // 년도 가져오기
  const itemDescriptions = useAppSelector((state) => state.Evl0105m.itemDescriptions);  // 평가항목설명 가져오기
  const role = useAppSelector(state => state.Evl0105m.role);                            // role 가져오기 

  const [prefix, setPrefix] = useState<'01' | '02' | 'null' >('null');                  // 주니어 시니어 코드 셋팅 주니어: 02 시니어: 01 
  const [isJunior, setIsJunior] = useState(false);                                      // 주니어인지 시니어인지 판단
  const [performanceData, setPerformanceData] = useState([]);                           // [업적 평가 데이터] 빈 배열로 초기화
  const [competencyData, setCompetencyData] = useState([]);                             // [역량 평가 데이터] 빈 배열로 초기화
  const [rawSearchData, setRawSearchData] = useState<any>(null);                        // 조회정보 저장
  const [checkData, setcheckData] = useState<any>(null);                                // 조회정보 저장
  const [userInfoTableData, setUserInfoTableData] = useState<UserInfoRow[]>([]);        // 본인 정보 테이블 상태
  const [evaluatorTableData, setevaluatorTableData] = useState<EvalInfoRow[]>([]);      // 평가자 테이블 상태 (항목/값 구조가 동일하다면 UserInfoRow 타입 재사용 가능)
  const [selectedJobGrCd, setSelectedJobGrCd] = useState("");                           // 직군 코드 상태  
  const [tableData, setTableData] = useState<RowData[]>([]);                            // 능력 개발 및 업무개선
  const [data, setData] = useState<TaskRow[]>([]);                                      // 업적 기술 테이블 상태
  const [loading, setLoading] = useState(true);                                         // 로딩
  const [dataSource2, setDatasource2] = useState([]);      
  const isJuniorRef = useRef(false);                                                    // 주니어일경우 treu 시니어인 경우 false 즉시저장
  const [isHidden, setIsHidden] = useState(true);                                       // 저장, 제출, 제출취소 숨김(화면랜더할때 깜빡일수있어서 기본 true)
  const [forceDisabled, setForceDisabled] = useState(false);                            // 화면 전체 디스에이블시
  // 미작성시 포커스이동
  const selectedJobGrCdRef = useRef<HTMLDivElement>(null);                                              // 직군코드(포커스 이동)
  const improvementRef = useRef<HTMLDivElement>(null);                                                  // 능력개발 기술 (포커스 이동)
  const performanceAndImprovementRef = useRef<HTMLDivElement>(null);                                    // 업적 기술(포커스 이동)
  const performanceRef = useRef<HTMLDivElement>(null);                                                  // 업적 평가 (포커스 이동)
  const competencyRef = useRef<HTMLDivElement>(null);                                                   // 역량 평가 (포커스 이동)

  // 포커스 이동함수
  const scrollToField = (ref: React.RefObject<HTMLDivElement>) => {
  if (ref && ref.current) {
    ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  };

  // 소계
  const performanceTotal = useMemo(() => calcTotal(performanceData), [performanceData]);    // 업적평가 소계
  const competencyTotal = useMemo(() => calcTotal(competencyData), [competencyData]);       // 역량평가 소계

  // 총계 및 평균 총점 계산    
  const grandTotal = useMemo(() => {                                                        
  // 소계가 문자열이므로 다시 숫자로 변환하여 더함                                                      
  const selfSum = Number(performanceTotal.self) + Number(competencyTotal.self);
  const firstSum = Number(performanceTotal.first) + Number(competencyTotal.first);
  const secondSum = Number(performanceTotal.second) + Number(competencyTotal.second);
  const avgTotal = secondSum;                                                               // 3개 총점의 평균 계산(2차평가자 점수만 반영으로 변경)

    return {
      weight: performanceTotal.weight + competencyTotal.weight,
      self: selfSum.toFixed(2),
      first: firstSum.toFixed(2),
      second: secondSum.toFixed(2),
      avg: avgTotal.toFixed(2), 
    };
  }, [performanceTotal, competencyTotal]);
    // 날짜 체크로직
    const isPast = (dateStr: string | null) => {
      if (!dateStr) return false; // 날짜가 없으면 체크 안 함
      const today = new Date();
      today.setHours(0, 0, 0, 0); // 시간 제외하고 날짜만 비교
      return new Date(dateStr) < today;
    };
  // 버튼 노출 로직
    const checkHidden = async () => {

      // 1. 데이터 부재 시 기본값 처리
      const item = rawSearchData;

      if (!item) return true; 
      // 2. 공통 수정 기간 만료 체크 (ModEdDt)
      const isModPast = 
        isPast(item.slfEvlModEdDt) || 
        isPast(item.eval1ModEdDt) || 
        isPast(item.eval2ModEdDt);

      // 3. 역할(Role)별 평가 기간 만료 체크 (EdDt)
      let isPeriodPast = false;
      if (role === "self") isPeriodPast = isPast(item.slfEvlEdDt);
      else if (role === "first") isPeriodPast = isPast(item.eval1EdDt);
      else if (role === "second") isPeriodPast = isPast(item.eval2EdDt);
      
      // 수정기간 체크 및 기간체크 
        if (isModPast || isPeriodPast) {
          return true;
        }
      // 4. 이미 제출했는지 여부(상위평가자만 체크)
      const isBlocked =  checkYn(item);
      // 5. evlEnableYn의 값이 N인경우 
      if(evlEditableYn === "N"){
        setForceDisabled(true); 
      }

      return isBlocked;
  
    };        
  

// 제출시 제출여부상태 변경저장
  const isSubmitted = useMemo(() => {
    if (!rawSearchData || Object.keys(rawSearchData).length === 0) return false;
    if (role === "self") return rawSearchData?.slfSubYn === "Y";
    if (role === "first") return rawSearchData?.eval1SubYn === "Y";
    if (role === "second") return rawSearchData?.eval2SubYn === "Y";
    return false;
  }, [role, rawSearchData]);

  const dispatch = useAppDispatch();

  // 브라우저 뒤로가기(popstate) 발생 시 모든 모달을 닫음
  useEffect(() => {
  const handlePopState = () => {
    Modal.destroyAll();
  };

    window.addEventListener('popstate', handlePopState);  // 이벤트리스너 등록
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    
      Modal.destroyAll();
    };
  }, []);
  
// 미리보기
useEffect(() => {
  if (props.viewMode === "preview") {
    loadPreview(); // 미리보기 전용 함수 호출
  } 
}, [props.viewMode]);

//미리보기 화면 시작
const loadPreview = async () => {
  setLoading(true);

  const targetEvlCd = props.evlType === 'HR-Junior' ? '02' : '01';      // 주니어면 '02', 아니면 (시니어) '01'
  const currentDefaultId = targetEvlCd === "02" ? "02010201" : "01010201";
  const requestData = { 
    evlCd: targetEvlCd, 
    // empNo: 'PREVIEW_USER', // 실제 사번 대신 더미값 사용
    isPreview: 'Y'
  };

   commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0019",
      success: (json: any) => { 
         const res = json.data ? json.data : json;
        //1. 능력개발 및 업무개선 매핑
      if (Array.isArray(res.improvementItems)) {
          const rows = res.improvementItems.map((it: any, idx: number) => ({
            key: String(idx + 1),
            category: it.itmNm,        // M.ITM_NM 항목명
            content: it.goalCntn ?? '',
            outcome: it.resultCntn ?? '',
            opinion: it.eval1Rmk ?? '',
            opinion2: it.eval2Rmk ?? '',
            hrEvlItmId: it.hrEvlItmId,isReadOnly: true, 
            disabled: true
        }));

        setTableData(rows);
      }
      // 2. 업적기술 매핑 
              if (Array.isArray(res.performanceItems) && res.performanceItems.length > 0) {
                const rows = res.performanceItems.map((it: any, idx: number) => ({
                  key: String(idx + 1),
                  taskName: it.taskNm ?? "",
                  weight: it.weightRt != null ? String(it.weightRt) : "",
                  objective: it.goalCntn ?? "",
                  performance: it.resultCntn ?? "",
                  effort: it.processCntn ?? "",
                  hrEvlItmId: it.hrEvlItmId || currentDefaultId // ID 저장 필수
                }));
                setData(rows);
              } else {
                // 기본 3행 유지 
                setData([
                  { key: "1", taskName: "", weight: "", objective: "", performance: "", effort: "" ,hrEvlItmId: currentDefaultId},
                ]);
              }
      },
   }

  );

    // ==========================================================
    // [트랜 2] TREVLR0021: 업적/역량 평가 등급(SelectBox) 조회
    // ==========================================================
    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0021", 
      success: (json: any) => {
        const res = json.data ? json.data : json;
        if (!res) return;

        // 백엔드에서 보낸 전체 리스트(allItems)를 가져옴  
        const allData = res.list;
      
        if (Array.isArray(allData)) {
      // ==========================================================
      // 업적 평가(Performance) 세팅 (ITM_TYP_CD === '01') ---
      // ==========================================================
      const perfMapped = allData.filter(
        (it: any) => it.itmTypCd === '01'&& it.hrEvlItmId !== '02010201' && it.hrEvlItmId !== '01010201' ) // 업적평가만 추출
        .map((it: any) => ({
          key: it.hrEvlItmId,           // DB의 고유 ID를 키로 사용
          hrEvlItmId: it.hrEvlItmId,
          dtlTypCdNm: it.dtlTypCd === '01' ? '수행업적' : '수행업적',
          item: it.itemNm,              // DB의 항목명
          description: it.itemDesc,     // DB의 항목설명
          weight: it.weightRt || 0,     // DB의 비중(BASE_ASGN)
          self: it.selfGrade || "",     // 저장된 점수 또는 빈값
          first: it.eval1Grade || "", 
          second: it.eval2Grade || ""
        }));
      setPerformanceData(perfMapped);   // 데이터를 직접 세팅
    // ==========================================================
    // 역량평가(Competency) 세팅
    // ==========================================================
     const compMapped = allData
      .filter((it: any) => it.itmTypCd === '02'&& it.hrEvlItmId !== '02010201' && it.hrEvlItmId !== '01010201' )
      .map((it: any) => {
        // 역량평가 구분 로직
        let dtlTypCdNm = '';
        if (it.dtlTypCd === '03') dtlTypCdNm = '공통역량';
        else if (it.dtlTypCd === '04') dtlTypCdNm = '역할역량';
        else if (it.dtlTypCd === '05') dtlTypCdNm = '직무역량';
        else dtlTypCdNm = '기타역량';

        return {
          key: it.hrEvlItmId,
          hrEvlItmId: it.hrEvlItmId,
          dtlTypCd: it.dtlTypCd,  // 코드비교용
          dtlTypCdNm: dtlTypCdNm, // 결정된 명칭 할당
          item: it.itemNm,
          description: it.itemDesc,
          weight: it.weightRt || 0,
          self: it.selfGrade || "",
          first: it.eval1Grade || "",
          second: it.eval2Grade || ""
        };
      });   
      setCompetencyData(compMapped);   // 데이터를 직접 세팅
    }
        setLoading(false);
      },
      error: () => setLoading(false)
    });
  };


  // 매핑조회함수 시작
  useEffect(() => {
    // 인사평가 메인화면에서 넘어왔는지 체크
    if(!isPreview ) {
      if(!props.hrMapId || !props.evlEnableYn) {
        Modal.confirm({
          title: "인사평가 메인페이지에서 접근하십시오.",
          okText: "확인",
          maskClosable: false,  //바깥 클릭 방지
          keyboard: false, // ESC 방지
          cancelButtonProps: { style: { display: 'none' } },      // 취소 버튼 숨김
          onOk: () => {
            common.openPage({ menuId: 'MEVL2025002' });           // 인사평가 메인 페이지로 이동
          },
          onCancel(){
            common.openPage({ menuId: 'MEVL2025002' });           // 인사평가 메인 페이지로 이동
          }
        });
        return;
        }
    }  
    // 로그인 사번 확인되면 즉시 조회
    if(!isPreview ) {
      if (hrMapId && loginEmpNo) {   
        loadEvalMap(hrMapId, loginEmpNo);
      } else {
        message.error("로그인 정보가 없습니다.");
      }
    }
  }, [loginEmpNo, hrMapId]);
useEffect(() => {
        // hidden 체크로직 실행
        if (!rawSearchData) return;

        const run = async () => {
          const result = await checkHidden();
        setIsHidden(result);
        };
        run();
      },[rawSearchData]);
/**
 * 기능 : loadEvalMap
 * 설명 : 전체 조회
 * param :
 * return : None
 **/
// 로그인사번과 empno가 있는지 확인
      const loadEvalMap = (mapId: string, empNo: string) => {
        setLoading(true); 
        if (!mapId || !empNo) {
          message.warning("조회 조건이 부족합니다. (hrMapId 또는 empNo)");
          setLoading(false); 
          return;
        }
      dispatch(Evl0105mAction.setInquiryCnds({ hrMapId: mapId, empNo , evlType}));     // 조회 조건 저장 (Redux 상태에는 원래 값 유지)

      const requestData = { hrMapId: mapId, empNo: empNo, evlYear };                   // props 값 사용

      commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0002",
      success: (evalMap: any) => { 
        const item = evalMap.performanceItems?.[0]; 
        setRawSearchData(item); // [데이터 담아두기]
        
        const postCd = item?.evlPostCd;
        const isJunior = postCd === '01' || postCd === '02';
        setIsJunior(isJunior);                                  // 화면 렌더링  true: 주니어 false: 시니어
        isJuniorRef.current = isJunior;                         // ref에  주니어일경우 true 시니어일경우 fasle 즉시 저장
        const defaultId = isJunior ? "02010201" : "01010201";   // true : 주니어 일시 02010201 시니어 false 01010201 셋팅 
        const isAdmin = common.isHrAdmin();                     // 관리자 권한
        const prefix = isJuniorRef.current ? '02' : '01';    // isJunior 직위코드가 01 or 02 일떄 트루면 선임이하 펄스면 책임이상
        setPrefix(prefix);                        // EVL_CD 선임이하 02 책임이상 01

        //권한 판별
        let role: "self" | "first" | "second" | "none" = "none";
        if (empNo === item?.empNo) {
          role = "self";
        } else if (empNo === item?.eval1EmpNo) {
          role = "first";
        } else if (empNo === item?.eval2EmpNo || isAdmin) {
          role = "second";
        }
      
        dispatch(Evl0105mAction.setRole(role));

        if (role !== "none") {
          // message.success(`(${role})권한으로 조회되었습니다.`);
        } else {
          message.warning("로그인 사번이 매핑에 없습니다.");
        }
        if (evlEnableYn === "N"){
          message.warning("수정 불가능 상태입니다.");
        }
        // 본인정보 테이블 데이터 업데이트
        setUserInfoTableData([
          {
            key: "1",
            항목1: "이름",
            값1: item?.empName || "",
            항목2: "직급",
            값2: item?.empDutyNm || "",
            항목3: "직위",
            값3: item?.evlPostNm || "",
          },
          {
            key: "2",
            항목1: "상위소속실",
            값1: item?.empUpperDeptNm || "",  //상위부서
            항목2: "소속실",
            값2: item?.empDeptNm || "",  //메인부서
            항목3: "직군",
            값3: jobGroupMap[String(item?.jobGrCd?.trim() || "none")] || "",
          },
        ]);

        // 평가자정보 테이블 데이터 업데이트
        setevaluatorTableData([
          {
            key: "1",
            차수: "1차",
            항목1: "이름",
            값1: item?.eval1Name || "",
            항목2: "직급",
            값2: item?.eval1DutyNm || "",
            항목3: "직위",
            값3: item?.evl1PostNm || "",
          },
          {
            key: "2",
            차수: "2차",
            항목1: "이름",
            값1: item?.eval2Name || "",  
            항목2: "직급",
            값2: item?.eval2DutyNm || "",  
            항목3: "직위",
             값3: item?.evl2PostNm || "",
          },
        ]);

       // 평가자 컬럼 셋팅
        setDatasource2([
        {
          key: "1",
          차수: "1차",
          이름: item?.eval1Name,
          직급: item?.eval1DutyNm ,
          직위: item?.evl1PostNm,
        },

        {
          key: "2",
          차수: "2차",
          이름: item?.eval2Name,
          직급: item?.eval2DutyNm ,
          직위: item?.evl2PostNm,
        },
      ]);
        // 1. 방어 코드: 필수 데이터가 없으면 로직 실행 안 함
        if (!hrMapId || !prefix ) return;
        const requestData = { 
            hrMapId, 
            prefix,
          };
          // ==========================================================
          // [트랜 1] TREVLR0019: 능력개발(1번) 및 업적기술(2번) 조회
          // ==========================================================
          commonProxy.callAsyncSvc(requestData, {

            txCd: "TREVLR0019",
            success: (json: any) => {
              const res = json.data ? json.data : json;
              const currentDefaultId = isJuniorRef.current ?  "02010201" : "01010201";  

              //1. 능력개발 및 업무개선 매핑
              if (Array.isArray(res.improvementItems)) {
                  const rows = res.improvementItems.map((it: any, idx: number) => ({
                    key: String(idx + 1),
                    category: it.itmNm,        // M.ITM_NM 항목명
                    content: it.goalCntn ?? '',
                    outcome: it.resultCntn ?? '',
                    opinion: it.eval1Rmk ?? '',
                    opinion2: it.eval2Rmk ?? '',
                    hrEvlItmId: it.hrEvlItmId,
                }));

                setTableData(rows);
              }

              // 2. 업적기술 매핑 
              if (Array.isArray(res.performanceItems) && res.performanceItems.length > 0) {
                const rows = res.performanceItems.map((it: any, idx: number) => ({
                  key: String(idx + 1),
                  taskName: it.taskNm ?? "",
                  weight: it.weightRt != null ? String(it.weightRt) : "",
                  objective: it.goalCntn ?? "",
                  performance: it.resultCntn ?? "",
                  effort: it.processCntn ?? "",
                  hrEvlItmId: it.hrEvlItmId || currentDefaultId // ID 저장 필수
                }));
                setData(rows);
              } else {
                // 기본 3행 유지 
                setData([
                  { key: "1", taskName: "", weight: "", objective: "", performance: "", effort: "" ,hrEvlItmId: currentDefaultId},
                ]);
              }
              
                // ==========================================================
                // [트랜 2] TREVLR0021: 업적/역량 평가 등급(SelectBox) 조회
                // ==========================================================
                commonProxy.callAsyncSvc(requestData, {
                  txCd: "TREVLR0021", 
                  success: (json: any) => {
                    const res = json.data ? json.data : json;
                    if (!res) return;

                    // 백엔드에서 보낸 전체 리스트(allItems)를 가져옴  
                    const allData = res.list;
                  
                    if (Array.isArray(allData)) {
                  // ==========================================================
                  // 업적 평가(Performance) 세팅 (ITM_TYP_CD === '01') ---
                  // ==========================================================
                  const perfMapped = allData.filter(
                    (it: any) => it.itmTypCd === '01'&& it.hrEvlItmId !== '02010201' && it.hrEvlItmId !== '01010201' ) // 업적평가만 추출
                    .map((it: any) => ({
                      key: it.hrEvlItmId,           // DB의 고유 ID를 키로 사용
                      hrEvlItmId: it.hrEvlItmId,
                      dtlTypCdNm: it.dtlTypCd === '01' ? '수행업적' : '수행업적',
                      item: it.itemNm,              // DB의 항목명
                      description: it.itemDesc,     // DB의 항목설명
                      weight: it.weightRt || 0,     // DB의 비중(BASE_ASGN)
                      self: it.selfGrade || "",     // 저장된 점수 또는 빈값
                      first: it.eval1Grade || "", 
                      second: it.eval2Grade || ""
                    }));
                  setPerformanceData(perfMapped);   // 데이터를 직접 세팅
                // ==========================================================
                // 역량평가(Competency) 세팅
                // ==========================================================
                const compMapped = allData
                  .filter((it: any) => it.itmTypCd === '02'&& it.hrEvlItmId !== '02010201' && it.hrEvlItmId !== '01010201' )
                  .map((it: any) => {
                    // 역량평가 구분 로직
                    let dtlTypCdNm = '';
                    if (it.dtlTypCd === '03') dtlTypCdNm = '공통역량';
                    else if (it.dtlTypCd === '04') dtlTypCdNm = '역할역량';
                    else if (it.dtlTypCd === '05') dtlTypCdNm = '직무역량';
                    else dtlTypCdNm = '기타역량';

                    return {
                      key: it.hrEvlItmId,
                      hrEvlItmId: it.hrEvlItmId,
                      dtlTypCd: it.dtlTypCd,  // 코드비교용
                      dtlTypCdNm: dtlTypCdNm, // 결정된 명칭 할당
                      item: it.itemNm,
                      description: it.itemDesc,
                      weight: it.weightRt || 0,
                      self: it.selfGrade || "",
                      first: it.eval1Grade || "",
                      second: it.eval2Grade || ""
                    };
                  });   
                  setCompetencyData(compMapped);   // 데이터를 직접 세팅
                }
                    setLoading(false);
                  },
                  error: () => setLoading(false)
                });
            },
            error: () => setLoading(false)
          }
        );
      },
      error: (err: any) => {
        console.error("API 실패:", err);
        message.error("평가자 매핑 조회에 실패했습니다.");
      },
    });
  };

// 직군 값 변경 핸들러
const handleUserInfoChange = (value: string, key: string, field: keyof UserInfoRow) => {
  setUserInfoTableData(prev =>
    prev.map(row => (row.key === key ? { ...row, [field]: value } : row))
  );
  // 변경된 필드가 '값3'(직군)일 때만 selectedJobGrCd 상태 업데이트
  if (field === '값3') {
    setSelectedJobGrCd(value);
  }
};

// 값 변경 핸들러
  const handleChange = (value: string, key: string, field: keyof RowData) => {
     let processedValue = value;
  // 1. 글자 수 체크 (500자 기준)
  if (value.length > 500) {
    // 500자까지만 자르기
    processedValue = value.slice(0, 500);

    // 2. 경고창 띄우기
    Modal.confirm({
      title: "※ 500자를 초과하였습니다.",
      content: "입력 내용은 500자 이내로 제한됩니다.", // 추가 설명 (선택 사항)
      okText: "확인",
      cancelButtonProps: { style: { display: 'none' } } 
    });
  }
  // 3. 상태 업데이트 (최종 처리된 값으로 저장)
    setTableData(prev =>
      prev.map(row => (row.key === key ? { ...row, [field]: value } : row))
    );
  };

  // 능력개발 및 업무개선 사항 기술 컬럼정의
  const columns = [
  {
      title: '구분',
      dataIndex: 'category',
      key: 'category',
      align: 'center'as const,
    },
    {
      title: '내용',
      dataIndex: 'content',
      key: 'content',
      align: 'center'as const,
      render: (_: any, record: RowData) => (
        <div style={{ paddingBottom: 10 }}>
        <TextArea
          key={record.key + (record.content || '')}
          defaultValue={record.content}
          disabled={role !== "self"|| evlEnableYn === "N" || forceDisabled} // 본인만 입력 가능
          onBlur={e => handleChange(e.target.value, record.key, 'content')} // 포커스가 나갈 때 한 번만 저장
          rows={isPreview ? 1 : 3}
          maxLength={500}   // 글자수 제한
          showCount         // 글자수 카운트 표시 (옵션)
        />
        </div>
      ),
    },
    {
      title: '성과',
      dataIndex: 'outcome',
      key: 'outcome',
      align: 'center'as const,
      render: (_: any, record: RowData) => (
         <div style={{ paddingBottom: 10 }}>
        <TextArea     
          key={record.key + (record.outcome || '')}
          defaultValue={record.outcome}
          disabled={role !== "self"|| evlEnableYn === "N"|| forceDisabled} // 본인만 입력 가능
          onBlur={e => handleChange(e.target.value, record.key, 'outcome')} // 포커스가 나갈 때 한 번만 저장
          rows={isPreview ? 1 : 3}
          maxLength={500}   // 글자수 제한
          // showCount         // 글자수 카운트 표시 (옵션)
          showCount={{
            formatter: ({ count, maxLength }) => `${count}/${maxLength}`
          }}
        />
        </div>
      ),
    },
   
    ...(role !== "self" ? [{
    title: '1차 평가자 의견',
    dataIndex: 'opinion',
    key: 'opinion',
    align: 'center'as const,
    render: (_: any, record: any) => (
      <TextArea
        key={record.key + (record.opinion || '')}
        defaultValue={record.opinion}
        disabled={role !== "first" || evlEnableYn === "N"|| forceDisabled} // 1차 평가자만 입력 가능
        onBlur={e => handleChange(e.target.value, record.key, 'opinion')} // 포커스가 나갈 때 한 번만 저장
        rows={isPreview ? 1 : 3}
        maxLength={500}   // 글자수 제한
        showCount         // 글자수 카운트 표시 (옵션)
      />
    ),
  }] : []),
  
    ...(role !== "self" && role !== "first" ? [{
      title: '2차 평가자 의견',
      dataIndex: 'opinion2',
      key: 'opinion2',
      align: 'center'as const,
      render: (_: any, record: any) => (
        <TextArea
          key={record.key + (record.opinion2 || '')}
          defaultValue={record.opinion2}
          disabled={role !== "second" || evlEnableYn === "N"|| forceDisabled}
          onBlur={e => handleChange(e.target.value, record.key, 'opinion2')}
          rows={isPreview ? 1 : 3}
          maxLength={500}
          showCount
        />
      ),
    }] : []),
  ];
  //미리보기
  const columns3 = [
  {
      title: '구분',
      dataIndex: 'category',
      key: 'category',
      align: 'center'as const,
    },
    {
      title: '내용',
      dataIndex: 'content',
      key: 'content',
      align: 'center'as const,
      render: (_: any, record: RowData) => (
        <div style={{ paddingBottom: 10 }}>
        <TextArea
          key={record.key + (record.content || '')}
          defaultValue={record.content}
          disabled={true} // 본인만 입력 가능
          onBlur={e => handleChange(e.target.value, record.key, 'content')} // 포커스가 나갈 때 한 번만 저장
          rows={isPreview ? 1 : 3}
          maxLength={500}   // 글자수 제한
          showCount         // 글자수 카운트 표시 (옵션)
        />
        </div>
      ),
    },
    {
      title: '성과',
      dataIndex: 'outcome',
      key: 'outcome',
      align: 'center'as const,
      render: (_: any, record: RowData) => (
         <div style={{ paddingBottom: 10 }}>
        <TextArea     
          key={record.key + (record.outcome || '')}
          defaultValue={record.outcome}
          disabled={true} // 본인만 입력 가능
          onBlur={e => handleChange(e.target.value, record.key, 'outcome')} // 포커스가 나갈 때 한 번만 저장
          rows={isPreview ? 1 : 3}
          maxLength={500}   // 글자수 제한
          // showCount         // 글자수 카운트 표시 (옵션)
          showCount={{
            formatter: ({ count, maxLength }) => `${count}/${maxLength}`
          }}
        />
        </div>
      ),
    },
   
    ...(role !== "self" ? [{
    title: '1차 평가자 의견',
    dataIndex: 'opinion',
    key: 'opinion',
    align: 'center'as const,
    render: (_: any, record: any) => (
      <TextArea
        key={record.key + (record.opinion || '')}
        defaultValue={record.opinion}
        disabled={true} // 1차 평가자만 입력 가능
        onBlur={e => handleChange(e.target.value, record.key, 'opinion')} // 포커스가 나갈 때 한 번만 저장
        rows={isPreview ? 1 : 3}
        maxLength={500}   // 글자수 제한
        showCount         // 글자수 카운트 표시 (옵션)
      />
    ),
  }] : []),
  
    ...(role !== "self" && role !== "first" ? [{
      title: '2차 평가자 의견',
      dataIndex: 'opinion2',
      key: 'opinion2',
      align: 'center'as const,
      render: (_: any, record: any) => (
        <TextArea
          key={record.key + (record.opinion2 || '')}
          defaultValue={record.opinion2}
          disabled={true}
          onBlur={e => handleChange(e.target.value, record.key, 'opinion2')}
          rows={isPreview ? 1 : 3}
          maxLength={500}
          showCount
        />
      ),
    }] : []),
  ];
  // 업적 기술 값변경
const perhandleChange = (value: string, key: string, field: keyof TaskRow) => {
  let processedValue = value;
  // 1. 글자 수 체크 (500자 기준)
  if (value.length > 500) {
    // 500자까지만 자르기
    processedValue = value.slice(0, 500);

    // 2. 경고창 띄우기
    Modal.confirm({
      title: "※ 500자를 초과하였습니다.",
      content: "입력 내용은 500자 이내로 제한됩니다.", // 추가 설명 (선택 사항)
      okText: "확인",
      cancelButtonProps: { style: { display: 'none' } } 
    });
  }
  // 3. 상태 업데이트 (최종 처리된 값으로 저장)
    setData(prev =>
      prev.map(row => (row.key === key ? { ...row, [field]: value } : row))
    );
  };

  const addRow = () => {
    const maxKey = data.length > 0 
      ? Math.max(...data.map(item => Number(item.key))) 
      : 0;
    const newKey = (maxKey + 1).toString();
    setData([...data, {
      key: newKey,
      taskName: '',
      weight: '',
      objective: '',
      performance: '',
      effort: '',
      hrEvlItmId: isJuniorRef.current ? "02010201" : "01010201" , 
    }]);
  };

    const deleteRow = (key: string) => {
      if (data.length <= 1) {
        message.warning("최소 한 개의 업적기술은 작성하셔야 합니다.");
        return;
      }
    setData(prev => prev.filter(row => row.key !== key));
  };


  const [error, setError] = useState<string | null>(null);
//업적기술 컬럼
const percolumns = [
    {
      title: '업무명',
      dataIndex: 'taskName',
      key: 'taskName',
      align: 'center'as const,
      render: (_: any, record: TaskRow) => (
        <div style={{ paddingBottom: 10 }}>
        <TextArea
          key={record.key + (record.taskName || '')}
          defaultValue={record.taskName}
          disabled={role !== "self" || evlEnableYn === "N"|| forceDisabled} // 본인만 입력 가능
          onBlur={e => perhandleChange(e.target.value, record.key, 'taskName')} // 포커스 나갈 때 저장 및 체크
          rows={isPreview ? 1 : 3}
          maxLength={500}   // 글자수 제한 (필요 시 제거 가능)
          showCount         // 글자수 카운트 표시
        />
        </div>
      ),
    },
    {
      title: '비중 (%)',
      dataIndex: 'weight',
      key: 'weight',
      align: 'center'as const,
      width: 70,
      render: (_: any, record: TaskRow) => (
        <Input
          value={record.weight || ''}
          disabled={role !== "self"|| evlEnableYn === "N"|| forceDisabled} // 본인만 입력 가능
          onChange={e => {
            // 숫자만 추출
            let raw = e.target.value.replace(/[^0-9]/g, '');
            // 100 초과 방지
            if (Number(raw) > 100) {
              raw = '100';
            }
            perhandleChange(raw, record.key, 'weight');
          }}
          onBlur={() => {
            // 합계 검증
            const total = data.reduce((sum, row) => sum + Number(row.weight || 0), 0);
            if (total > 100) {
              setError(`비중 합계가 100%를 초과했습니다. 현재 합계: ${total}%`);
              scrollToField(performanceAndImprovementRef);
              } else {
                setError(null);
              }
            }}
            //addonAfter="%"
        />
      ),
    },
    {
      title: '업무목표',
      dataIndex: 'objective',
      key: 'objective',
      align: 'center'as const,
      render: (_: any, record: TaskRow) => (
        <div style={{ paddingBottom: 10 }}>
        <TextArea
          key={record.key + (record.objective || '')}
          defaultValue={record.objective}
          disabled={role !== "self"|| evlEnableYn === "N"|| forceDisabled} // 본인만 입력 가능
          onBlur={e => perhandleChange(e.target.value, record.key, 'objective')}  // 포커스가 나갈 때 한 번만 저장
          rows={isPreview ? 1 : 3}
          maxLength={500}   // 글자수 제한
          showCount         // 글자수 카운트 표시 (옵션)
        />
        </div>
      ),
    },
    {
      title: '업무성과',
      dataIndex: 'performance',
      key: 'performance',
      align: 'center'as const,
      render: (_: any, record: TaskRow) => (
        <div style={{ paddingBottom: 10 }}>
        <TextArea
          key={record.key + (record.performance || '')}
          defaultValue={record.performance}
          disabled={role !== "self"|| evlEnableYn === "N"|| forceDisabled} // 본인만 입력 가능
          onBlur={e => perhandleChange(e.target.value, record.key, 'performance')}  // 포커스가 나갈 때 한 번만 저장
          rows={isPreview ? 1 : 3}
          maxLength={500}   // 글자수 제한
          showCount         // 글자수 카운트 표시 (옵션)
        />
        </div>
      ),
    },
    {
      title: '성과에 대한 과정, 노력',
      dataIndex: 'effort',
      key: 'effort',
      align: 'center'as const,
      render: (_: any, record: TaskRow) => (
        <div style={{ paddingBottom: 10 }}>
        <TextArea
          key={record.key + (record.effort || '')}
          defaultValue={record.effort}
          disabled={role !== "self"|| evlEnableYn === "N"|| forceDisabled} // 본인만 입력 가능
          onBlur={e => perhandleChange(e.target.value, record.key, 'effort')}  // 포커스가 나갈 때 한 번만 저장
          rows={isPreview ? 1 : 3}
          maxLength={500}   // 글자수 제한
          showCount         // 글자수 카운트 표시 (옵션)
        />
        </div>
      ),
    },

    {
      title: '삭제',
      key: 'action',
      align: 'center'as const,
      render: (_: any, record: TaskRow) => (
        <Button 
          danger 
          size="small"
          disabled={isSubmitted || role !== 'self' || evlEnableYn === "N"|| forceDisabled}
          onClick={() => {
            Modal.confirm({
              title: "정말로 이 행을 삭제하시겠습니까?", // 출력할 메시지
              okText: "확인",
              cancelText: "취소",
              // 확인 버튼을 눌렀을 때 실행
              onOk: () => {
                deleteRow(record.key); 
              },
              // 취소 버튼을 눌렀을 때 실행
              onCancel: () => {
            
              }
            });
          }}
    >
      삭제
    </Button>
  ),
},
  ];
   
  // 업적평가 컬럼
  const performanceColumns = (setData: React.Dispatch<React.SetStateAction<any[]>>, itemDescriptions: Record<string, string>) => [
    {
      title: '구분',
      dataIndex: 'dtlTypCdNm',
      key: 'dtlTypCdNm',
      align: 'center'as const,
      width: 100,
      render: (value: string, record: any, index: number) => {
        //소계행 처리
        if (record.key === 'perf-total') {
          return { children: `${record.category} ${record.item}`, props: { colSpan: 2 } };
        }
      // 첫 번째 행에서만 글자를 보여주고 나머지 행을 합침
      // performanceTableData에서 'perf-total'을 제외한 실제 데이터 개수를 계산
      if (index === 0) {
        return {
          children: value || '수행업적',
          props: { 
            // 데이터 배열에서 소계(total)를 제외한 순수 항목 개수만큼 rowSpan 설정
            rowSpan: performanceTableData.length - 1, 
          }
        };
      }
     // 3. 두 번째 행부터는 rowSpan을 0으로 주어 위 행에 합쳐지게 함
      if (index > 0 && record.key !== 'perf-total') {
        return { props: { rowSpan: 0 } };
      }
        return value;
      },
    },
    {
      title: '평가항목',
      dataIndex: 'item',
      key: 'item',
      width: 150,
      align: 'center'as const,
      render: (value: string, record: any) => {
        if (record.key === 'perf-total') return { children: '', props: { colSpan: 0 } };
        return value;
      },
    },
    { 
      title: '비중 (%)', 
      dataIndex: 'weight', 
      key: 'weight',
      align: 'center'as const,
      width: 70, 
      render: (value: string, record: any) => {
        if (record.key === 'perf-total') {
            return { children: value ,
              props: { colSpan: 1 } };
        }
        return value;
      }
    },
    {
      title: '평가항목 설명',
      dataIndex: 'description',
      key: 'description',
      align: 'center'as const,
      width: 1000,
      render: (value: string, record: any) => {
        if (record.key === 'perf-total') {
            return { 
              children: '', 
              props: { colSpan: 1 } }; 
        }
        // 일반 행인 경우
        const displayValue = value || itemDescriptions[record.hrEvlItmId] || "";
        return (
        <div style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
          {displayValue}
        </div>
      );
      },
    },
    {
      title: '본인 평가',
      dataIndex: 'self',
      key: 'self',
      align: 'center'as const,
      render: (_: any, record: any) => {
        // 소계 행일 때는 점수만 표시
        if (record.key === 'perf-total' ) {
          return record.self;
        }
        // 일반 행일 때는 Select 표시
        return (
          <Select
            style={{ width: 80 }}
            value={record.self}
            disabled={role !== "self"|| evlEnableYn === "N"|| forceDisabled || isPreview}   // 본인만 활성화
            onChange={(val) => handleSelectChange(val, record.key, 'self', setData)}
          >
            {gradeOptions.map(opt => <Select.Option key={opt} value={opt}>{opt}</Select.Option>)}
          </Select>
        );
      }
    },

...(role !== "self" ? [{
  title: '1차 평가',
  dataIndex: 'first',
  key: 'first',
  align: 'center'as const,
  render: (_: any, record: any) => {
    if (record.key === 'perf-total' ) {
      return record.first;
    }
    return (
      
      <Select
        style={{ width: 80 }}
        value={record.first}
        disabled={role !== "first"|| evlEnableYn === "N"|| forceDisabled || isPreview}  // 1차 평가자만 활성화
        onChange={(val) => handleSelectChange(val, record.key, 'first', setData)}
      >
        {gradeOptions.map(opt => <Select.Option key={opt} value={opt}>{opt}</Select.Option>)}
      </Select>
    );
  }
}] : []),

...(role !== "self" && role !== "first" ? [{
  title: '2차 평가',
  dataIndex: 'second',
  key: 'second',
  align: 'center'as const,
  render: (_: any, record: any) => {
    if (record.key === 'perf-total' || record.key === 'comp-total') {
      return record.second;
    }
    return (
      <Select
        style={{ width: 80 }}
        value={record.second}
        disabled={role !== "second" || evlEnableYn === "N" || forceDisabled || isPreview} // 2차 평가자만 활성화
        onChange={(val) => handleSelectChange(val, record.key, 'second', setData)}
      >
        {gradeOptions.map(opt => <Select.Option key={opt} value={opt}>{opt}</Select.Option>)}
      </Select>
    );
  }
}] : []),
  ];

// 업적/역량 테이블 데이터에 소계 행 추가
  const performanceTableData = [
    ...performanceData,
    {
      key: 'perf-total',
      category: '소계',
      item: '',
      weight: performanceTotal.weight,
      description: '',
      self: performanceTotal.self,
      first: performanceTotal.first,
      second: performanceTotal.second,
      isTotal: true,        
      hrEvlItmId: 'TOTAL',  
      selfGrade: ''      ,  
      eval1Grade: '',   
      eval2Grade: '',  
    },
  ];
  // 역량평가 컬럼
  const competencyColumns = (setData: React.Dispatch<React.SetStateAction<any[]>>, itemDescriptions: Record<string, string>) => [
    {
      title: '구분',
      dataIndex: 'dtlTypCdNm',
      key: 'dtlTypCdNm',
      align: 'center'as const,
      width: 100,
      render: (value: string, record: any, index: number) => {
        // 소계 행: 구분+평가항목을 하나의 셀로 병합
        if (record.key === 'comp-total') {
          return { children: `${record.category} ${record.item}`, props: { colSpan: 2 } };
        }
        const isFirstInGroup = index === 0 || competencyTableData[index - 1].dtlTypCd !== record.dtlTypCd;
        if (isFirstInGroup) {
        // 현재 그룹(같은 dtlTypCd)이 몇 개인지 카운트 (소계 행 전까지만)
        let groupCount = 0;
        for (let i = index; i < competencyTableData.length; i++) {
          if (competencyTableData[i].key !== 'comp-total' && competencyTableData[i].dtlTypCd === record.dtlTypCd) {
            groupCount++;
          } else {
            break;
          }
        }
        return {
         children: value || record.typeNm || '역량평가',
          props: { 
            rowSpan: groupCount, 
          }
        };
      } else {
        // 그룹의 첫 번째 행이 아니면 보이지 않게 처리
        return { props: { rowSpan: 0 } };
      }
    },
  },
    {
      title: '평가항목',
      dataIndex: 'item',
      key: 'item',
      width: 150,
      align: 'center'as const,
      render: (value: string, record: any) => {
        // 소계 행: 이미 합쳐서 보여주므로 숨김
        if (record.key === 'comp-total') return { children: '', props: { colSpan: 0 } };
        return value;
      },
    },
    { 
      title: '비중 (%)',
      dataIndex: 'weight',
      key: 'weight',
      align: 'center'as const,
      width: 70,
      render: (value: string, record: any) => {
        if (record.key === 'comp-total') {
            return { children: value, props: { colSpan: 1 } };
        }
        return value;
      }
    },
    {
      title: '평가항목 설명',
      dataIndex: 'description',
      key: 'description',
      align: 'center'as const,
      width: 1000,
      render: (value: string, record: any) => {
        if (record.key === 'comp-total') return { children: '', props: { colSpan: 1 } };
        const displayValue = value || itemDescriptions[record.hrEvlItmId] || "";
        return (
        <div style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
          {displayValue}
        </div>
      );
      },
    },
    {
      title: '본인 평가',
      dataIndex: 'self',
      key: 'self',
      align: 'center'as const,
      render: (_: any, record: any) => {
        //소계 행일 때는 합산된 점수만 표시
        if (record.key === 'comp-total') {
          return <span>{record.self}</span>;
        }
        return (
          <Select 
            style={{ width: 80 }} 
            value={record.self} 
            disabled={role !== "self"|| evlEnableYn === "N" || forceDisabled|| isPreview} 
            onChange={(val) => handleSelectChange(val, record.key, 'self', setData)}
          >
            {gradeOptions.map(opt => <Select.Option key={opt} value={opt}>{opt}</Select.Option>)}
          </Select>
        );
    }
    },
    
...(role !== "self" ? [{
      title: '1차 평가',
      dataIndex: 'first',
      key: 'first',
      align: 'center'as const,
      render: (_: any, record: any) => {
        if (record.key === 'comp-total') {
          return <p>{record.first}</p>;
        }
        return (
          <Select 
            style={{ width: 80 }} 
            value={record.first} 
            disabled={role !== "first"|| evlEnableYn === "N" || forceDisabled || isPreview} 
            onChange={(val) => handleSelectChange(val, record.key, 'first', setData)}
          >
            {gradeOptions.map(opt => <Select.Option key={opt} value={opt}>{opt}</Select.Option>)}
          </Select>
        );
     }
}] : []),
   
    ...(role !== "self" && role !== "first" ? [{
      title: '2차 평가',
      dataIndex: 'second',
      key: 'second',
      align: 'center'as const,
      render: (_: any, record: any) => {
        if (record.key === 'comp-total') {
          return <p>{record.second}</p>;
        }
        return (
          <Select 
            style={{ width: 80 }} 
            value={record.second} 
            disabled={role !== "second" || evlEnableYn === "N" || forceDisabled || isPreview} 
            onChange={(val) => handleSelectChange(val, record.key, 'second', setData)}
          >
            {gradeOptions.map(opt => <Select.Option key={opt} value={opt}>{opt}</Select.Option>)}
          </Select>
        );
    }
    }] : []),
  ];

// 역량평가 테이블 데이터 + 소계 행
  const competencyTableData = [
    ...competencyData,
    {
      key: 'comp-total',
      category: '소계',
      item: '',
      weight: competencyTotal.weight,
      description: '',
      self: competencyTotal.self,
      first: competencyTotal.first,
      second: competencyTotal.second,
      isTotal: true,        
      hrEvlItmId: 'TOTAL',   
      selfGrade: '',  
      eval1Grade: '',  
      eval2Grade: '',        
    },
  ];


// ROLE별 제출여부 차단
const blockKeyMap: Record<string, string[]> = {
  self: ["slfSubYn", "eval1SubYn"],     // 자기,1차 평가제출
  first: ["eval1SubYn", "eval2SubYn"],  // 1차,2차 평가제출
  second: ["eval2SubYn"],               // 2차평가 제출 
};

// hidden용 체크로직
const checkYn = (item: any): boolean => {
    if (!item) return false;
    let isBlocked = false;
    let msg = "";

    if (role === "self") {
      // 자기평가 → 1차평가 제출 여부 체크
      isBlocked = item?.eval1SubYn === "Y";
      if (isBlocked) {
        msg = "1차 평가자가 이미 제출하여 자기평가 저장 및 제출이 불가능합니다.";
      }

    } else if (role === "first") {
      // 1차평가 → 2차평가 제출 여부 체크
      isBlocked = item?.eval2SubYn === "Y";
      if (isBlocked) {
        msg = "2차 평가자가 이미 제출하여 1차 평가 저장 및 제출이 불가능합니다.";
      }

    } else if (role === "second") {
      // 2차평가 → 점수조정 사유 존재 여부 체크
      isBlocked = item?.scrAdjRsn !== null && item?.scrAdjRsn !== "";
      if (isBlocked) {
        msg = "점수조정 사유가 존재하여 2차 평가 저장 및 제출이 불가능합니다.";
      }
    }

    if (isBlocked) {
      Modal.confirm({
        title: "저장 및 제출이 불가능합니다.",
        content: msg,
        okText: "확인",
        cancelButtonProps: { style: { display: "none" } },
        onOk: () => {
          setForceDisabled(true);
        }
      });
      return true;  // 차단
    }
    return false; // 통과
};
// 저장 및 제출 체크
const checkSubmitYn = (): Promise<{ isBlocked: boolean, res: any }> => {

  return new Promise((resolve, reject) => {
  const requestData = { hrMapId, evlYear};

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0002",
      success: (json: any) => {

        const res = json.data ? json.data : json;
        if (!res?.performanceItems) {
          resolve({ isBlocked: false, res: null });
          return;
        }
       
        let isBlocked = false;
        let msg = "";
        const blockKeys = blockKeyMap[role] || [];
        if (role === "second") {
          // 2차평가자일땐 점수조정 사유 존재 여부 체크 추가
          isBlocked = res?.performanceItems[0]?.scrAdjRsn !== null;
        } else {
          isBlocked = res.performanceItems.some((item: any) => blockKeys.some((key) => item[key] === "Y")
          );
        }
        if (isBlocked) {
         
          if (role === "self") {
          // 자기평가 저장 시
          if (res?.performanceItems[0]?.eval1SubYn === "Y") {
            msg = "1차 평가자가 이미 제출하여 자기평가 저장 및 제출이 불가능합니다.";
          } else if (res?.performanceItems[0]?.slfSubYn === "Y") {
            msg = "자기평가가 이미 제출 완료되어 저장 및 제출이 불가능합니다.";
          }
          } else if (role === "first") {
          // 1차 평가 저장 시
          if (res?.performanceItems[0]?.eval2SubYn === "Y") {
            msg = "2차 평가자가 이미 제출하여 1차 평가 저장 및 제출이 불가능합니다.";
          } else if (res?.performanceItems[0]?.eval1SubYn === "Y") {
            msg = "1차 평가가 이미 제출 완료되어 저장 및 제출이 불가능합니다.";
          }
          } else if (role === "second") {
          // 2차 평가 저장 시
          if (res?.performanceItems[0]?.scrAdjRsn !== null && res?.performanceItems[0]?.scrAdjRsn !== "") {
            msg = "점수조정 사유가 존재하여 2차 평가 저장 및 제출이 불가능합니다."; 
          } else if (res?.performanceItems[0]?.eval2SubYn === "Y") {
            msg = "2차 평가가 이미 제출 완료되어 저장 및 제출이 불가능합니다.";
          }
          }
            Modal.confirm({
              title: "저장 및 제출이 불가능합니다.",
              content: msg,
              okText: "확인",
              cancelButtonProps: { style: { display: 'none' } }, 
              onOk: () => {
                setForceDisabled(true);
                resolve({ isBlocked: true, res });
              }
              });
              return;
            }
            resolve({ isBlocked: false, res });
      },
      error: (err: any) => {
        message.error("제출여부 조회중 에러가 발생하였습니다.");
        reject(err);
      }
    });
  });
};
// 제출취소용 저장 직전 체크로직(상위평가자 제출여부만 체크)
const checkSubmitYn2 = (): Promise<{ isBlocked: boolean, res: any }> => {
  return new Promise((resolve, reject) => {
  const requestData = { hrMapId, evlYear };
    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0002",

      success: (json: any) => {
        const res = json.data ? json.data : json;
        const item = res?.performanceItems?.[0];
  
         setcheckData(res);   
        if (!res?.performanceItems) {
          resolve({ isBlocked: false, res: null });
          return;
        }
        let isBlocked = false;

        if (role === "self") {
          // 1차 평가자가 제출했는지 체크
          isBlocked = res.performanceItems.some((item: any) => item.eval1SubYn === "Y");
        }

        if (role === "first") {
          // 2차 평가자가 제출했는지 체크
          isBlocked = res.performanceItems.some((item: any) => item.eval2SubYn === "Y");
        }

        if (role === "second") {
          // 점수조정 사유 존재 여부 체크
          isBlocked = res.performanceItems.some((item: any) => item.scrAdjRsn !== null);
        }

        if (isBlocked) {
          Modal.confirm({
          title: "제출취소가 불가능합니다.",
          content: "상위평가자가 이미 제출 또는 점수조정을 완료했습니다.\n상위평가자에게 문의하세요.",
          okText: "확인",
          cancelButtonProps: { style: { display: 'none' } }, 
          onOk: () => {
            resolve({ isBlocked: true, res }); 
          }
          });
          return
        }
        resolve({ isBlocked: false, res });
      },
      error: (err: any) => {
        message.error("제출여부 조회중 에러가 발생하였습니다.");
        reject(err);
      }
    });
  });
};
 /**
   * 기능 : checkdate
   * 설명 : 변경된 제출기한 체크
   * param :
   * return : None
   **/
  const checkdate = (res: any) => {
    const today = new Date();
    today.setHours(0,0,0,0);
    let targetDate: string | null = null;

    if (!res || !res.performanceItems[0] || res.performanceItems.length === 0) {
      
      return false;
    }
    const item = res.performanceItems[0];
   
    if (role === "self") {
       if (item.slfEvlModEdDt !== null && item.slfEvlModEdDt !== undefined && item.slfEvlModEdDt !== "") {
       targetDate = item.slfEvlModEdDt;
      }else{
        targetDate = item.slfEvlEdDt;
      }
    } else if (role === "first") {
      if (item.evl1EvlModEdDt !== null && item.evl1EvlModEdDt !== undefined && item.evl1EvlModEdDt !== "") {
        targetDate = item.evl1EvlModEdDt;
      }else{
         targetDate = item.eval1EdDt;
      }
    } else if (role === "second") {
      if (item.evl2EvlModEdDt !== null && item.evl2EvlModEdDt !== undefined && item.evl2EvlModEdDt !== "") {
        targetDate = item.evl2EvlModEdDt;
      }else{
         targetDate = item.eval2EdDt;
      }
    }

    if (!targetDate) return true;

    const year = Number(targetDate.substring(0,4));
    const month = Number(targetDate.substring(4,6)) - 1; // JS month는 0~11
    const day = Number(targetDate.substring(6,8));
    const checkDate = new Date(year, month, day);

    if (checkDate < today) {
      return false;
    }
    return true;
  }
  

// 저장
const handleSave = async () => {

  const { isBlocked, res } = await checkSubmitYn();
  // 제출여부 체크
   if (isBlocked) {
      common.openPage({ menuId: 'MEVL2025002' });
      return;
    }
    // 제출기한 체크
  if(!checkdate(res)){
      alert("평가 기간이 만료되었습니다.");
      common.openPage({ menuId: 'MEVL2025002' });
      return;
  }
    const targetEmpNo = rawSearchData?.empNo;     //피평가자 사번담기
    // 직군코드 코드로 변경
    const jobGroupRow = userInfoTableData.find(row => row.항목3 === '직군');  //직군 행 찾기
    const selectedJobGrNm = jobGroupRow ? jobGroupRow.값3 : "";              //찾은 행 담기(예:전산전문직)
    const selectedJobGrCd = Object.keys(jobGroupMap).find(
      (key) => jobGroupMap[key] === selectedJobGrNm
    ) || ""; // 매칭되는게 없으면 빈값

  // 저장한 rawSearchData가 있는지 확인
    if (!rawSearchData) {
      message.error("조회된 데이터가 없습니다. 먼저 조회를 완료해주세요.");
      return;
  }
  // 1. 업적기술 데이터 셋팅
  const performanceItems = data.map((row, idx) => ({
    hrMapId: hrMapId,
    hrEvlItmId: row.hrEvlItmId || (isJuniorRef ? "02010201" : "01010201"),
    writeSeq: String(idx + 1).padStart(2, '0'),
    taskNm: row.taskName,
    goalCntn: row.objective,
    resultCntn: row.performance,
    processCntn: row.effort,
    weightRt: row.weight ? Number(row.weight) : null,
    lstChgEmpNo: loginEmpNo,
    lstChgGuid: crypto.randomUUID(),
  }));
 
  // 2. 능력개발 및 업무 개선 사항 데이터 셋팅
  const improvementItems = tableData.map((row, idx) => ({
    hrMapId: hrMapId,
    hrEvlItmId: row.hrEvlItmId ,   //row 내부의 ID를 바로 꺼내옵니다.
    writeSeq: String(idx + 1).padStart(2, '0'),
    goalCntn: row.content,
    resultCntn: row.outcome,
    eval1Rmk: row.opinion,
    eval2Rmk: row.opinion2,
    lstChgEmpNo: loginEmpNo,
    lstChgGuid: crypto.randomUUID(),
    jobGrCd: selectedJobGrCd,
    }));

    // 3. 업적 평가 데이터 가공 (S, A, B, C, D 등급 포함)
  const performanceEvalItems = performanceData
    .filter(item => item.hrEvlItmId !== 'TOTAL')
    .map((item) => ({
      hrMapId: hrMapId,
      hrEvlItmId: item.hrEvlItmId, 
      selfGrade: item.self || "",     //DB에서 selfGrade, eval1Grade, eval2Grade
      eval1Grade: item.first || "", 
      eval2Grade: item.second || "",
    }));

  // 4. 역량 평가 데이터 가공
  const competencyEvalItems = competencyData
  .filter(item => item.hrEvlItmId !== 'TOTAL')
    .map((item) => ({
      hrMapId: hrMapId,
      hrEvlItmId: item.hrEvlItmId,
      selfGrade: item.self || "",    
      eval1Grade: item.first || "",
      eval2Grade: item.second || "",
    }));

  const payload = {
    ...rawSearchData,    //performanceItems[0]의 데이터를 담아둠
    role,                // 현재 작성자
    hrMapId,
    hrEvlItmId,
    jobGrCd: selectedJobGrCd,   //직군코드
    empNo: loginEmpNo,    
    performanceItems,     //업적기술 데이터 셋팅
    performanceData,      //업적평가 데이터
    improvementItems,     //능력개발 및 업무 개선 사항 데이터 셋팅
    performanceEvalItems, // 업적 평가 점수 데이터가공
    competencyEvalItems,  // 역량 평가 점수 데이터가공
    competencyData,       //역량평가 데이터
    tableData,            //본인정보 기본 테이블
    // ...saveFlags,         //제출여부 N  -> 삭제예정 2026.03.06
    slfEvlTotScr : grandTotal.self,
    eval1TotScr :grandTotal.first,  // EVAL1_TOT_SCR
    eval2TotScr : grandTotal.second, // EVAL2_TOT_SCR
    fnlScr: grandTotal.avg,      // FNL_SCR
    evlYear,
    prefix: `${prefix}` ,// 선임이하:02 책임이상:01 EVL_CD
    targetEmpNo: targetEmpNo//피평가자 empno
  };

  commonProxy.callAsyncSvc(payload, {
    
    txCd: "TREVLS0001",   // 저장용 트랜잭션 코드
    success: (res: any) => {
      message.success("저장되었습니다.");
    },
    error: (err: any) => {
      message.error("저장 중 오류 발생");
    },
  });
};

const handleCancelSubmit = async () => {

  const { isBlocked, res } = await checkSubmitYn2();
    // 제출취소 체크
  if (isBlocked) {
      common.openPage({ menuId: 'MEVL2025002' });
      return;
    }
    // 제출기한 체크
  if(!checkdate(res)){
      alert("평가 기간이 만료되었습니다.");
      common.openPage({ menuId: 'MEVL2025002' });
      return;
  }

  // 제출취소시 룰별 업데이트 키
  let submitKey = "";
  if (role === "self") {
    submitKey = "slfSubYn";
  } else if (role === "first") {
    submitKey = "eval1SubYn";
  } else if (role === "second") {
    submitKey = "eval2SubYn";
  }
  
  const requestData = {
  hrMapId,
  evlYear,
  [submitKey]: "N"
};

    commonProxy.callAsyncSvc(requestData, {
    txCd: "TREVLS0002",
    success: () => {
      message.success("제출 취소가 완료되었습니다.");
      common.openPage({ menuId: 'MEVL2025002' });
    },
    error: (err: any) => {
      console.error("제출 취소 중 에러 발생:", err);
      message.error("제출 취소 중 에러가 발생하였습니다.");
    }
  });
}

// 제출로직
  const handleSubmit = async () => {

  const { isBlocked, res } = await checkSubmitYn();

  // 제출여부 체크
   if (isBlocked) {
      common.openPage({ menuId: 'MEVL2025002' });
      return;
    }
    // 제출기한 체크
  if(!checkdate(res)){
      alert("평가 기간이 만료되었습니다.");
      common.openPage({ menuId: 'MEVL2025002' });
      return;
  }
    const targetEmpNo = rawSearchData?.empNo;     //피평가자 사번담기
      // 직군코드 코드로 변경
    const jobGroupRow = userInfoTableData.find(row => row.항목3 === '직군');  //직군 행 찾기
    const selectedJobGrNm = jobGroupRow ? jobGroupRow.값3 : "";              //찾은 행 담기(예:전산전문직)
    const selectedJobGrCd = Object.keys(jobGroupMap).find(
      (key) => jobGroupMap[key] === selectedJobGrNm
    ) || "";        // 매칭되는게 없으면 빈값

    const submitStatus = {
        slfSubYn: rawSearchData.slfSubYn || 'N',
        eval1SubYn: rawSearchData.eval1SubYn || 'N',
        eval2SubYn: rawSearchData.eval2SubYn || 'N',
        // 현재 역할에 따라 해당 필드만 'Y'로 업데이트
        ...(role === "self" && { slfSubYn: "Y" }),
        ...(role === "first" && { eval1SubYn: "Y" }),
        ...(role === "second" && { eval2SubYn: "Y" }),
      };

    // 저장한 rawSearchData가 있는지 확인
      if (!rawSearchData) {
        message.error("조회된 데이터가 없습니다.");
        return;
    }
    // 1. 업적기술 데이터 셋팅
    const performanceItems = data.map((row, idx) => ({
      hrMapId: hrMapId,
      hrEvlItmId: row.hrEvlItmId || hrEvlItmId  ,
      writeSeq: String(idx + 1).padStart(2, '0'),
      taskNm: row.taskName,
      goalCntn: row.objective,
      resultCntn: row.performance,
      processCntn: row.effort,
      weightRt: row.weight ? Number(row.weight) : null,
      lstChgEmpNo: loginEmpNo,
      lstChgGuid: crypto.randomUUID(),
    }));
 
    // 2. 능력개발 및 업무 개선 사항 데이터 셋팅
    const improvementItems = tableData.map((row, idx) => ({
      hrMapId: hrMapId,
      hrEvlItmId: row.hrEvlItmId || hrEvlItmId  ,   //row 내부의 ID를 바로 꺼내옵니다.
      writeSeq: String(idx + 1).padStart(2, '0'),
      goalCntn: row.content,
      resultCntn: row.outcome,
      eval1Rmk: row.opinion,
      eval2Rmk: row.opinion2,
      lstChgEmpNo: loginEmpNo,
      lstChgGuid: crypto.randomUUID(),
      jobGrCd: selectedJobGrCd,
    }));

    // 3. 업적 평가 데이터 가공 (S, A, B, C, D 등급 포함)
    const performanceEvalItems = performanceData
      .map((item) => ({
        hrMapId: hrMapId,
        hrEvlItmId: item.hrEvlItmId, 
        selfGrade: item.self || "",    
        eval1Grade: item.first || "", 
        eval2Grade: item.second || "",
      }));

    // 4. 역량 평가 데이터 가공
    const competencyEvalItems = competencyData
      .map((item) => ({
        hrMapId: hrMapId,
        hrEvlItmId: item.hrEvlItmId,
        selfGrade: item.self || "",    
        eval1Grade: item.first || "",
        eval2Grade: item.second || "",
      }));

    const payload = {
      ...rawSearchData,
      ...submitStatus,
      role,                      // 작성자
      hrMapId,
      hrEvlItmId,
      empNo: loginEmpNo,
      jobGrCd: selectedJobGrCd,
      performanceItems,
      performanceData,
      improvementItems,
      performanceEvalItems, // 업적 평가
      competencyEvalItems,  // 역량 평가 
      competencyData,
      tableData,
      slfEvlTotScr : grandTotal.self,
      eval1TotScr :grandTotal.first,    // EVAL1_TOT_SCR
      eval2TotScr : grandTotal.second,  // EVAL2_TOT_SCR
      fnlScr: grandTotal.avg,           // FNL_SCR
      evlYear ,
      prefix: `${prefix}` ,// 선임이하:02 책임이상:01 EVL_CD
      targetEmpNo: targetEmpNo//피평가자 empno
    };

// 체크로직 시작
// 업적기술 비중 합계 체크
  const totalWeight = data.reduce((sum, row) => sum + Number(row.weight || 0), 0);
  
  // 100% 검증 로직
  if (totalWeight !== 100) {
    message.error(`업적기술의 비중 합계가 100%여야 제출할 수 있습니다. (현재: ${totalWeight}%)`);
    // 입력 중인 에러 메시지 칸이 있다면 거기에도 표시
    setError(`비중 합계가 100%가 아닙니다. (${totalWeight}%)`);
    scrollToField(performanceAndImprovementRef);
    return; 
  }
  // 비중이 0인 항목이 있는지 체크
  const hasZeroWeight = data.some(row => Number(row.weight || 0) === 0);
  if (hasZeroWeight) {
    message.error("비중이 0%인 항목은 제출할 수 없습니다. 내용을 입력하거나 삭제해 주세요.");
    return;
  }
//  직군 선택 체크
  if (!selectedJobGrCd || selectedJobGrCd.trim() === "" || selectedJobGrCd === "none") {
    message.error("직군을 선택해주세요.");
    scrollToField(selectedJobGrCdRef);
    return;
  }

  //  능력개발 및 업무 개선 사항 체크 (tableData 상태값 검사)
  // 내용, 성과, 1차 평가자 의견 필수 (2차 평가자 의견인 opinion2는 체크 제외)
  const isImprovementValid = tableData.every((row) => {
  const isBasicFill = row.content?.trim() && row.outcome?.trim();
    // role이 'self'인 경우: 내용과 성과만 있으면 통과
        if (role === 'self') {
            return isBasicFill;
        }
        if (role === 'first') {
            return (
                row.content?.trim() && 
                row.outcome?.trim() && 
                row.opinion?.trim()
            );
        }
        if (role === 'second') {
            return true; // 2차 평가자는 의견 안 써도 제출 가능
        }
        return true;
  });
  if (!isImprovementValid) {
      message.error("능력개발 및 업무 개선 사항 기술을 모두 입력해주세요.");
      scrollToField(improvementRef);
      return;
  }

  //  업적기술서 체크 (data 상태값 검사)
  // 업무명, 비중, 목표, 성과, 과정/노력 중 하나라도 비어있는 행이 있는지 확인
  const isPerformanceValid = data.every((row) => {
      return (
          row.taskName?.trim() && 
          row.weight && 
          row.objective?.trim() && 
          row.performance?.trim() && 
          row.effort?.trim()
      );
  });
  if (!isPerformanceValid) {
      message.error("업적기술을 모두 입력해주세요.");
      scrollToField(performanceAndImprovementRef);
      return;
  }

//  업적 평가 미입력 항목 체크
  const unevaluatedPerformance = performanceData.filter(
    (item) =>{
        if (role === 'self') return !item.self;   // 본인이면 본인 점수 체크
        if (role === 'first') return !item.first; // 1차 평가자면 1차 점수 체크
        if (role === 'second') return !item.second; // 2차 평가자면 2차 점수 체크
    }
  );

  if (unevaluatedPerformance.length > 0) {
    message.error({
      content: `업적 평가 항목 중 평가하지 않은 항목이 ${unevaluatedPerformance.length}개 있습니다.`,
      duration: 3,
    });
    scrollToField(performanceRef);
    return;
  }

  //  역량 평가 미입력 항목 체크
  const unevaluatedCompetency = competencyData.filter(
    (item) =>  {
        if (role === 'self') return !item.self;   // 본인이면 본인 점수 체크
        if (role === 'first') return !item.first; // 1차 평가자면 1차 점수 체크
        if (role === 'second') return !item.second; // 2차 평가자면 2차 점수 체크
    }
  );

  if (unevaluatedCompetency.length > 0) {
    message.error({
      content: `역량 평가 항목 중 평가하지 않은 항목이 ${unevaluatedCompetency.length}개 있습니다.`,
      duration: 3,
    });
    scrollToField(competencyRef);
    return;
  }


  commonProxy.callAsyncSvc(payload, {
    txCd: "TREVLS0001",   // 저장용 트랜잭션 코드
    success: (res: any) => {
      message.success("제출되었습니다.");

      const param = {
          menuId: 'MEVL2025002', // 평가 목록(Main) 메뉴 ID
          props: {
            empNo   : common.getEmpNo(), 
            evlYear : evlYear
          }
        };
        setTimeout(() => {
          common.openPage(param);
        }, 500);

    },
    error: (err: any) => {
      console.error("제출 실패:", err);
      message.error("제출 중 오류 발생");
    },
  });
 
};


  // prefix가 아직 null인 경우 화면을 막음
  if (prefix === null) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center',
        height: '80vh' // 화면 중앙에 배치
      }}>
        <Spin size="large" tip="사용자 권한 및 데이터를 확인 중입니다..." />
      </div>
    );
  }

const columns2 = [
  { title: '평가차수', dataIndex: '차수', key: '차수', width: 100, align: 'center' as const},
  { title: '이름', dataIndex: '이름', key: '이름', width: 100, align: 'center' as const},
  { title: '직급', dataIndex: '직급', key: '직급', width: 100, align: 'center'as const },
  { title: '직위', dataIndex: '직위', key: '직위', width: 100, align: 'center' as const},
];




//미리보기 컴포넌트
// 미리보기 전용 서브 컴포넌트
const PreviewContent = ({ performanceData, competencyData, tableData, evlEnableYn: propsEvlEnableYn  }:any) => {
  useEffect(() => {
    if(props.viewMode ==="preview"){
      const evlEnableYn = "N";
      dispatch(Evl0105mAction.setRole("second"));
    }    
  }, [dispatch]);
  const previewColumns = [
    { title: '구분', dataIndex: 'itemNm', key: 'itemNm', width: 200, align: 'center' as const },
    { title: '평가항목', dataIndex: 'itemName', key: 'itemName', align: 'center' as const },
    { title: '비중', dataIndex: 'weight', key: 'weight', width: 100, align: 'center' as const },
    { title: '항목설명', dataIndex: 'itemDesc', key: 'itemDesc' },
  ];
    return (
      <>
      {<h1 style={{ textAlign: 'center', marginBottom: 32 }}>인사평가</h1> }
     
      {/* 능력개발 및 업무 개선 사항 기술 */}
          <h3>능력개발 및 업무 개선 사항 기술</h3>
          <div ref={improvementRef}>
            <Table
              columns={columns3}
              dataSource={tableData}
              pagination={false}
              bordered
              size="small"
            />
          </div>

          {/* 업적 기술 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>업적(Performance) 기술</h3>
            <Button type="primary" onClick={addRow} disabled={true} >
              + 업무 추가
            </Button>
          </div>
          <div ref={performanceAndImprovementRef}>
            <Table
              columns={percolumns}
              dataSource={data}
              pagination={false}
              bordered
              size="small"
              rowKey="key"
            />
          </div>
          {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

          {/* 업적 평가 */}
          <h3>업적(Performance) 평가</h3>
          <div ref={performanceRef}>
            <Table
              columns={performanceColumns(setPerformanceData, itemDescriptions)}
              dataSource={performanceTableData}
              pagination={false}
              bordered
              size="small"
            />
          </div>

          {/* 역량 평가 */}
          <h3>역량(Competency) 평가</h3>
          <div ref={competencyRef}>
            <Table
              columns={competencyColumns(setCompetencyData, itemDescriptions)}
              dataSource={competencyTableData}
              pagination={false}
              bordered
              size="small"
            />
          </div>
    </>
   
  );
};

// 컴포넌트의 return 시작
  return (
    <div style={{ margin: 20, padding: 20 }}>
      {/* 1. 로딩 상태 체크 */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <Spin size="large" tip="데이터를 불러오는 중입니다..." />
        </div>
      ) : props.viewMode ? (
        /* 2. 미리보기 모드일 때 */
        <PreviewContent 
          performanceData={performanceTableData}
          competencyData={competencyTableData}
          tableData={tableData}
        />
      ) : (
        /* 3. 실제 평가 화면 (기본 모드) */
        <>
          <h1 style={{ textAlign: 'center', marginBottom: 16 }}>{evlYear}년도 인사 평가표</h1>
          
          {/* 본인 정보 */}
          <h3>본인</h3>
          <div ref={selectedJobGrCdRef}>
            <Table
              dataSource={userInfoTableData}
              columns={[
                { title: '', dataIndex: '항목1', key: '항목1', width: 100, align: 'center' },
                { title: '', dataIndex: '값1', key: '값1', align: 'center' },
                { title: '', dataIndex: '항목2', key: '항목2' , width: 100, align: 'center' },
                { title: '', dataIndex: '값2', key: '값2', align: 'center' },
                { title: '', dataIndex: '항목3', key: '항목3' , width: 100, align: 'center' },
                { title: '', dataIndex: '값3', key: '값3', align: 'center', 
                  // 직군셀렉트용
                  render: (text: string, record: any, index: number) => {   
                    if (record.항목3 === '직군') {
                      return (
                        <Select<string>
                          value={text || undefined}
                          onChange={(value) => handleUserInfoChange(value, record.key, '값3')}
                          disabled={role !== 'self' || evlEnableYn === "N" || forceDisabled}
                          placeholder="선택"
                          style={{ width: 160 }}
                        >
                          <Select.Option value="전산전문직">전산전문직</Select.Option>
                          <Select.Option value="비즈니스컨설턴트">비즈니스컨설턴트</Select.Option>
                          <Select.Option value="일반직">일반직</Select.Option>
                        </Select>
                      );
                    }
                    return text;
                  },
                },
              ]}
              pagination={false}
              bordered
              size="small"
              showHeader={false}
            />
          </div>

          <div style={{ display: "flex", gap: "15px" }}>
            {/* 평가자 정보 */}
            <div style={{ flex: 1 }}>
            <h3>평가자</h3>
            <Table
              columns={columns2}
              dataSource={dataSource2}
              pagination={false}
              bordered
              size="small"
            />
            </div>
            {/* 평가 결과 종합표 */}
            <div style={{ flex: 1 }}>
            <h3>평가 결과 종합표</h3>
              <Table
                columns={[
                  { title: '구분', dataIndex: 'category', key: 'category', align: 'center' as const },
                  { title: '비중', dataIndex: 'weight', key: 'weight' , align: 'center' as const },
                  { title: '본인 평가', dataIndex: 'self', key: 'self', align: 'center' as const },
                  ...(role !== "self" ? [{ title: '1차 평가', dataIndex: 'first', key: 'first', align: 'center' as const }] : []),
                  ...(role !== "self" && role !== "first" ? [{ title: '2차 평가', dataIndex: 'second', key: 'second', align: 'center' as const }] : []),
                ]}
                dataSource={[
                  { key: '1', category: '업적평가', weight: performanceTotal.weight, self: performanceTotal.self, first: performanceTotal.first, second: performanceTotal.second },
                  { key: '2', category: '역량평가', weight: competencyTotal.weight, self: competencyTotal.self, first: competencyTotal.first, second: competencyTotal.second },
                  { key: '3', category: '총계', weight: grandTotal.weight, self: grandTotal.self, first: grandTotal.first, second: grandTotal.second },
                ]}
                pagination={false}
                bordered
                size="small"
              />
            </div>
          </div>

          {/* 능력개발 및 업무 개선 사항 기술 */}
          <h3>능력개발 및 업무 개선 사항 기술</h3>
          <div ref={improvementRef}>
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={false}
              bordered
              size="small"
            />
          </div>

          {/* 업적 기술 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>업적(Performance) 기술</h3>
            <Button type="primary" onClick={addRow} disabled={isSubmitted || role !== 'self' || evlEnableYn === "N" || forceDisabled}>
              + 업무 추가
            </Button>
          </div>
          <div ref={performanceAndImprovementRef}>
            <Table
              columns={percolumns}
              dataSource={data}
              pagination={false}
              bordered
              size="small"
              rowKey="key"
            />
          </div>
          {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

          {/* 업적 평가 */}
          <h3>업적(Performance) 평가</h3>
          <div ref={performanceRef}>
            <Table
              columns={performanceColumns(setPerformanceData, itemDescriptions)}
              dataSource={performanceTableData}
              pagination={false}
              bordered
              size="small"
            />
          </div>

          {/* 역량 평가 */}
          <h3>역량(Competency) 평가</h3>
          <div ref={competencyRef}>
            <Table
              columns={competencyColumns(setCompetencyData, itemDescriptions)}
              dataSource={competencyTableData}
              pagination={false}
              bordered
              size="small"
            />
          </div>

          {isHidden || forceDisabled ? null : (
            <>
          {(isSubmitted || role === "none" || evlEnableYn === "N") ? (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 32 }}>
            <Button
              type="primary" danger
              onClick={() => {
                Modal.confirm({
                  title: "제출을 취소하시겠습니까?",
                  okText: "확인",
                  cancelText: "취소",
                  onOk: () => { handleCancelSubmit(); }
                });
              }}
            >
              제출취소
            </Button>
            </div>
          ) : (
            <>
              {/* 저장 버튼 */}
              <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: 32 }}>
              <Button
                type="primary"
                onClick={() => {
                  Modal.confirm({
                    title: "저장하시겠습니까?",
                    okText: "확인",
                    cancelText: "취소",
                    onOk: () => { handleSave(); }
                  });
                }}
              >
                저장
              </Button>

              {/* 제출 버튼 */}
              <Button
                type="primary"
                onClick={() => {
                  Modal.confirm({
                    title: "제출하시겠습니까?",
                    okText: "확인",
                    cancelText: "취소",
                    onOk: () => { handleSubmit(); }
                  });
                }}
              >
                제출
              </Button>
              </div>
            </>
          )}
          </>
          )}

        </> // 실제 평가 화면 끝
      )} 
    </div>
  ); 
};
